import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_voiture-lamarsa/artifacts/krmorh2a_image.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#about', label: t('nav.about') },
    { href: '#fleet', label: t('nav.fleet') },
    { href: '#reviews', label: t('nav.reviews') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            data-testid="logo-link"
            className="flex items-center group"
            onClick={(e) => scrollToSection(e, '#home')}
          >
            {isScrolled ? (
              <img 
                src={LOGO_URL} 
                alt="UNI CAR - rent a car" 
                className="h-14 w-auto"
              />
            ) : (
              <div className="flex items-center gap-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2">
                  <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
                    <path d="M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M3 11h18v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7z" stroke="white" strokeWidth="1.5"/>
                    <circle cx="7" cy="15" r="2" stroke="white" strokeWidth="1.5"/>
                    <circle cx="17" cy="15" r="2" stroke="white" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div>
                  <span className="text-xl font-bold font-['Outfit'] tracking-tight text-white">
                    UNI CAR
                  </span>
                  <p className="text-xs text-white/80">rent a car</p>
                </div>
              </div>
            )}
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.replace('#', '')}`}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all hover:bg-white/10 ${
                  isScrolled
                    ? 'text-foreground hover:bg-accent'
                    : 'text-white hover:bg-white/20'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Language Selector & Mobile Menu */}
          <div className="flex items-center gap-2">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="language-selector"
                  className={`flex items-center gap-2 rounded-full ${
                    isScrolled
                      ? 'text-foreground hover:bg-accent'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-sm font-medium">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`language-option-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${
                      language === lang.code ? 'bg-accent' : ''
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-button"
              className={`md:hidden rounded-full ${
                isScrolled
                  ? 'text-foreground hover:bg-accent'
                  : 'text-white hover:bg-white/20'
              }`}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t"
          >
            <div className="py-4 px-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.href.replace('#', '')}`}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block px-4 py-3 rounded-lg text-foreground hover:bg-accent transition-colors font-medium"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
